import { useEffect, useState } from 'react';

const listeners = new Set();
const timers = new Map();
let toastId = 0;
let items = [];

function publish() {
  listeners.forEach((listener) => listener(items));
}

function subscribe(listener) {
  listeners.add(listener);
  listener(items);

  return () => {
    listeners.delete(listener);
  };
}

function dismiss(id) {
  if (timers.has(id)) {
    clearTimeout(timers.get(id));
    timers.delete(id);
  }

  items = items.filter((item) => item.id !== id);
  publish();
}

function showToast(type, message, duration = 4500) {
  const normalizedMessage = String(message ?? '').trim();

  if (!normalizedMessage) {
    return null;
  }

  const id = `toast-${toastId += 1}`;
  items = [...items, { id, type, message: normalizedMessage }];
  publish();

  const timerId = setTimeout(() => dismiss(id), duration);
  timers.set(id, timerId);

  return id;
}

export const toast = {
  success(message, options = {}) {
    return showToast('success', message, options.duration);
  },
  error(message, options = {}) {
    return showToast('error', message, options.duration);
  },
  message(message, options = {}) {
    return showToast('default', message, options.duration);
  },
  dismiss,
};

function getPositionClasses(position) {
  if (position === 'top-left') {
    return 'left-4 top-4 items-start';
  }

  if (position === 'bottom-left') {
    return 'bottom-4 left-4 items-start';
  }

  if (position === 'bottom-right') {
    return 'bottom-4 right-4 items-end';
  }

  return 'right-4 top-4 items-end';
}

function getToastClasses(type, richColors) {
  if (!richColors) {
    return 'border-[#d8dde6] bg-white text-[#1f2937]';
  }

  if (type === 'success') {
    return 'border-[#b8e5c7] bg-[#edf9f1] text-[#0f6b37]';
  }

  if (type === 'error') {
    return 'border-[#f3c1b6] bg-[#fff1ed] text-[#b24427]';
  }

  return 'border-[#d8dde6] bg-white text-[#1f2937]';
}

function ToastIcon({ type }) {
  if (type === 'success') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        className="h-4 w-4 fill-current"
      >
        <path d="M9.55 16.6 5.4 12.45l-1.4 1.4 5.55 5.55L20 8.95l-1.4-1.4z" />
      </svg>
    );
  }

  if (type === 'error') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        className="h-4 w-4 fill-current"
      >
        <path d="M11 7h2v7h-2zm0 8h2v2h-2z" />
        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2V9h2v5z" />
      </svg>
    );
  }

  return null;
}

export function Toaster({
  position = 'top-right',
  richColors = false,
}) {
  const [visibleItems, setVisibleItems] = useState(items);

  useEffect(() => subscribe(setVisibleItems), []);

  return (
    <div
      className={`pointer-events-none fixed z-[999999] flex w-full max-w-sm flex-col gap-3 ${getPositionClasses(position)}`}
      aria-live="polite"
      aria-atomic="true"
    >
      {visibleItems.map((item) => (
        <div
          key={item.id}
          className={`pointer-events-auto flex w-full items-start gap-3 rounded-xl border px-4 py-3 shadow-[0_12px_30px_rgba(15,23,42,0.14)] ${getToastClasses(item.type, richColors)}`}
          role="status"
        >
          <span className="mt-[2px] flex h-5 w-5 shrink-0 items-center justify-center">
            <ToastIcon type={item.type} />
          </span>

          <p className="flex-1 text-sm font-semibold leading-6">
            {item.message}
          </p>

          <button
            type="button"
            onClick={() => dismiss(item.id)}
            className="shrink-0 text-base leading-none opacity-60 transition hover:opacity-100"
            aria-label="Bildirimi kapat"
          >
            x
          </button>
        </div>
      ))}
    </div>
  );
}
